import cn from 'classnames';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@features/campaigns/shared/components/Spinner/Spinner.tsx';
import { Review } from '@features/users/adminPanel/components/BillingInfo/Review/Review.tsx';
import formStyles from '@features/users/adminPanel/components/Form/Form.module.scss';
import { Form } from '@features/users/adminPanel/components/Form/Form.tsx';
import { FormValues } from '@features/users/adminPanel/components/Form/Form.types.ts';
import { FormFooter } from '@features/users/adminPanel/components/Form/FormFooter.tsx';
import { FormFields } from '@features/users/adminPanel/components/FormFields/FieldTypes.ts';
import { useUserProfileMutation } from '@features/users/adminPanel/hooks/useUserProfileMutation.tsx';
import { InfoPanel } from '@shared/components/InfoPanel/InfoPanel.tsx';
import { InfoPanelTypes } from '@shared/components/InfoPanel/InfoPanel.types.ts';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { useUserProfileData } from '@shared/hooks/useUserProfileData.ts';

import styles from './BillingInfoSection.module.scss';

export function BillingInfoSection() {
  const { t } = useTranslation([i18nNameSpace.USERS]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleToggleModalVisibility = () => setIsModalVisible((prevState) => !prevState);

  const {
    userProfileData,
    userProfileDataIsPending,
    isFormVisible,
    canEditUserProfile,
    isUserProfileDataApproved,
    isBillingInfoProvided,
  } = useUserProfileData({ canFetchUserProfileData: true });

  const {
    postUserProfileMutation,
    userProfileMutationIsPending,
    putUserProfileMutation,
    putUserProfileMutationIsPending,
  } = useUserProfileMutation();

  const possibleFormInitialValues = {
    [FormFields.INVOICING_EMAIL]: '',
    [FormFields.VAT_NUMBER]: userProfileData?.vatNumber || '',
  };

  const editFormInitialValues = {
    [FormFields.INVOICING_EMAIL]: userProfileData?.invoicingEmail || '',
    [FormFields.VAT_NUMBER]: userProfileData?.vatNumber || '',
  };

  const handleEditUserProfileSubmit = (data: FormValues) => {
    if (userProfileData) {
      putUserProfileMutation(
        {
          id: userProfileData.id,
          payload: {
            [FormFields.INVOICING_EMAIL]: data[FormFields.INVOICING_EMAIL],
            [FormFields.VAT_NUMBER]: data[FormFields.VAT_NUMBER],
          },
        },
        {
          onSuccess: () => handleToggleModalVisibility(),
        },
      );
    }
  };

  return (
    <section className={styles.wrapper}>
      <header className={styles.header}>
        <h2 className={styles.text}>{t('page.adminPanel.headers.billingInfo')}</h2>
        {canEditUserProfile && (
          <Button onClick={handleToggleModalVisibility} className={styles.editButton} outlined>
            <i className={cn('pi pi-pencil', styles.pencilIcon)}></i>
            {t('page.adminPanel.common.edit')}
          </Button>
        )}
      </header>
      <div className={styles.cardWrapper}>
        {userProfileDataIsPending && <Spinner variant='elementOverlay' />}
        <Review
          data={userProfileData}
          isFormVisible={isFormVisible}
          isUserProfileDataApproved={isUserProfileDataApproved}
          isBillingInfoProvided={isBillingInfoProvided}
        />
        {isFormVisible && (
          <Form submitCallback={postUserProfileMutation} initialValues={possibleFormInitialValues}>
            <FormFooter isPending={userProfileMutationIsPending} dataTestId={'post-user-profile-submit'} />
          </Form>
        )}
      </div>

      {isUserProfileDataApproved && (
        <InfoPanel type={InfoPanelTypes.INFO} className={styles.infoPanel}>
          <p>{t('page.adminPanel.infoPanels.editBillingInfo')}</p>
        </InfoPanel>
      )}

      <Dialog
        visible={isModalVisible}
        onHide={handleToggleModalVisibility}
        draggable={false}
        resizable={false}
        header={t('page.adminPanel.headers.editBillingInfo')}
      >
        <Form
          submitCallback={handleEditUserProfileSubmit}
          className={formStyles.modalForm}
          initialValues={editFormInitialValues}
        >
          <FormFooter
            isPending={putUserProfileMutationIsPending}
            dataTestId={'edit-billing-info-submit'}
            cancelCallback={handleToggleModalVisibility}
            submitButtonText={'page.adminPanel.common.save'}
          />
        </Form>
      </Dialog>
    </section>
  );
}
